/**
 * Typed form for the `discharge-interaction` encounter type.
 *
 * Reachable only via the `discharge-member` activity. Steps: Details →
 * Next-Task Assignment. No Type picker (the activity fixes the type), no
 * Notes step (discharge encounters are bookkeeping events that capture
 * timing/method only).
 *
 * @agentic connections
 */

import { Skeleton, Stack } from "@mantine/core";
import { CallDocumentationField } from "@/components/domain/care-time/CallDocumentationField";
import { useCallTimeCareSelection } from "../hooks/useCallTimeCareSelection";
import { useForm } from "@mantine/form";
import { forwardRef, useMemo } from "react";
import type { Encounter } from "@medplum/fhirtypes";

import { getCarePlanActivityDisplay } from "@benchmark/core/constants";

import { FormStep, SteppedForm } from "@benchmark/ui/components/atomic";
import { useFormSubmission } from "@benchmark/ui/hooks";
import type {
  DischargeInteractionFormData,
  EncounterActivityContext,
} from "@benchmark/core/types";
import type { AppliedBusinessRule } from "@benchmark/core/resolvers";

import type { EncounterFormRef } from "../EncounterFormShell";
import { useNonIntakeFormScaffold } from "../hooks/useNonIntakeFormScaffold";
import { EncounterDetailsStep } from "../shared/EncounterDetailsStep";
import { NextTaskAssignmentStep } from "../shared/NextTaskAssignmentStep";
import {
  buildDirectionOptions,
  buildMethodOptions,
} from "../shared/encounterIcons";
import { useDischargeInteractionSubmit } from "./useDischargeInteractionSubmit";

const METHOD_OPTIONS = buildMethodOptions();
const DIRECTION_OPTIONS = buildDirectionOptions();

/**
 * Props for `DischargeInteractionForm`.
 *
 * @agentic connections
 */
export type DischargeInteractionFormProps = {
  patientId?: string;
  encounter?: Encounter;
  onSuccess?: (encounterId: string) => void;
  onCancel?: () => void;
  activityContext?: EncounterActivityContext;
  restrictToEncounterTypes?: string[];
  nextActivityLabel?: string;
  nextActivityDefaultOwner?: { ref: string; display: string };
  nextTaskOwnerOverride?: { ref: string; display: string } | null;
  onNextTaskOwnerOverrideChange?: (
    owner: { ref: string; display: string } | null,
  ) => void;
  nextActivityAppliedRule?: AppliedBusinessRule;
  isResolvingNextTaskOwner?: boolean;
};

/**
 * Typed form for `discharge-interaction` encounters.
 *
 * @agentic connections
 */
export const DischargeInteractionForm = forwardRef<
  EncounterFormRef,
  DischargeInteractionFormProps
>(function DischargeInteractionForm(
  {
    patientId: propPatientId,
    encounter: propEncounter,
    onSuccess,
    onCancel,
    activityContext,
    nextActivityLabel,
    nextActivityDefaultOwner,
    nextTaskOwnerOverride,
    onNextTaskOwnerOverrideChange,
    nextActivityAppliedRule,
    isResolvingNextTaskOwner = false,
  },
  ref,
) {
  const form = useForm<DischargeInteractionFormData>({
    initialValues: {
      "encounter-type": "discharge-interaction",
      period: { start: "", end: "" },
      "encounter-method": "",
      "encounter-inout": "",
    },
  });

  const {
    patient,
    existingEncounter,
    isEditing,
    isLoadingEdit,
    practitionerRoles,
    ConfirmationModal,
    markAsClean,
  } = useNonIntakeFormScaffold({
    type: "discharge-interaction",
    form,
    propPatientId,
    propEncounter,
    activityContext,
    nextTaskOwnerOverride,
    ref,
  });

  const careTime = useCallTimeCareSelection();

  const handleSubmit = useDischargeInteractionSubmit({
    patient,
    existingEncounter,
    practitionerRoles,
    activityContext,
    onSuccess,
    onSubmitted: markAsClean,
    getCareTimeMinutes: careTime.getCareTimeMinutes,
    getCareTimeCommunicationId: careTime.getCareTimeCommunicationId,
    getCareTimeEffectivePeriod: careTime.getCareTimeEffectivePeriod,
  });

  const { createSubmissionHandler, isSubmitting } = useFormSubmission();

  const steps = useMemo(
    () => [
      {
        id: "details",
        title: "Details",
        subtitle: "Review and confirm the discharge interaction details.",
        requiredFields: [
          "period.start",
          "period.end",
          "encounter-method",
          "encounter-inout",
        ] as string[],
      },
      ...(activityContext
        ? [
            {
              id: "next-task-assignment",
              title: "Assignment",
              subtitle: "Review next task assignment",
            },
          ]
        : []),
    ],
    [activityContext],
  );

  const submitText = activityContext?.activityCode
    ? getCarePlanActivityDisplay(activityContext.activityCode)
    : isEditing
      ? "Update Discharge"
      : "Save Discharge";

  if (!patient || isLoadingEdit) {
    return (
      <div data-testid="discharge-interaction-form">
        <Stack gap="md">
          <Skeleton height={32} width="40%" />
          <Skeleton height={36} />
          <Skeleton height={44} width="200px" />
        </Stack>
        <ConfirmationModal />
      </div>
    );
  }

  return (
    <div data-testid="discharge-interaction-form">
      <SteppedForm
        form={form}
        steps={steps}
        onSubmit={createSubmissionHandler(handleSubmit)}
        isSubmitting={isSubmitting}
        submitText={submitText}
        onCancel={onCancel}
      >
        <FormStep id="details">
          <Stack gap="md">
            <EncounterDetailsStep
              form={form}
              methodOptions={METHOD_OPTIONS}
              directionOptions={DIRECTION_OPTIONS}
              billableOptions={[]}
              requiresBillable={false}
            />
            {!activityContext && (
              <CallDocumentationField
                patientId={patient?.id}
                minutes={careTime.minutes}
                onMinutesChange={careTime.setMinutes}
                selectedCommunicationId={careTime.selectedCall?.communicationId}
                onSelectCall={careTime.selectCall}
              />
            )}
          </Stack>
        </FormStep>

        <FormStep id="next-task-assignment">
          <Stack gap="md">
            <NextTaskAssignmentStep
              form={form}
              nextActivityLabel={nextActivityLabel}
              nextActivityDefaultOwner={nextActivityDefaultOwner}
              nextTaskOwnerOverride={nextTaskOwnerOverride}
              onNextTaskOwnerOverrideChange={onNextTaskOwnerOverrideChange}
              nextActivityAppliedRule={nextActivityAppliedRule}
              isResolvingNextTaskOwner={isResolvingNextTaskOwner}
            />
            <CallDocumentationField
              patientId={patient?.id}
              minutes={careTime.minutes}
              onMinutesChange={careTime.setMinutes}
              selectedCommunicationId={careTime.selectedCall?.communicationId}
              onSelectCall={careTime.selectCall}
            />
          </Stack>
        </FormStep>
      </SteppedForm>

      <ConfirmationModal />
    </div>
  );
});
